export type RobotNotificationPrefsMode = 'inherit' | 'mute_all' | 'custom'

export type NotifyGroupMeta = {
  id: number
  name: string
  event_types: string[]
}

export type NotificationPrefsMeta = {
  notify_groups: NotifyGroupMeta[]
  error_groups: string[]
  modes: RobotNotificationPrefsMode[]
  error_groups_need_revision?: boolean
  error_groups_unknown_in_fms?: string[]
  error_groups_missing_from_coda?: string[]
  /** Coda groups not in the code catalog and not yet accepted on this instance. */
  error_groups_pending_accept?: string[]
}

export type UserNotificationPrefs = {
  muted_until: string | null
  timezone: string
  quiet_hours_enabled: boolean
  quiet_hours_start: string | null
  quiet_hours_end: string | null
  /** Server-computed: local time is inside quiet window right now. */
  quiet_hours_active?: boolean
  allow_notify_group_ids: number[]
  allow_error_groups: string[]
  updated_at?: string
}

export type UserNotificationPrefsPatch = Partial<
  Pick<
    UserNotificationPrefs,
    | 'muted_until'
    | 'timezone'
    | 'quiet_hours_enabled'
    | 'quiet_hours_start'
    | 'quiet_hours_end'
    | 'allow_notify_group_ids'
    | 'allow_error_groups'
  >
>

export type UserRobotNotificationPrefs = {
  robot_id: number
  robot_name: string
  robot_serial: string
  mode: RobotNotificationPrefsMode
  allow_notify_group_ids: number[]
  allow_error_groups: string[]
  updated_at?: string
}

export type UserRobotNotificationPrefsWrite = {
  mode: RobotNotificationPrefsMode
  allow_notify_group_ids?: number[]
  allow_error_groups?: string[]
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = `HTTP ${res.status}`
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') detail = body.detail
      else if (body) detail = JSON.stringify(body)
    } catch {
      /* ignore */
    }
    throw new Error(detail)
  }
  if (res.status === 204) {
    return undefined as T
  }
  return (await res.json()) as T
}

export async function fetchNotificationPrefs(): Promise<UserNotificationPrefs> {
  const res = await fetch('/api/notifications/prefs/', { credentials: 'include' })
  return parseJson(res)
}

export async function patchNotificationPrefs(
  data: UserNotificationPrefsPatch
): Promise<UserNotificationPrefs> {
  const res = await fetch('/api/notifications/prefs/', {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return parseJson(res)
}

export async function fetchNotificationPrefsMeta(): Promise<NotificationPrefsMeta> {
  const res = await fetch('/api/notifications/prefs/meta/', { credentials: 'include' })
  return parseJson(res)
}

export async function acceptCodaDiagnosticGroups(): Promise<{ accepted: string[]; pending: string[] }> {
  const res = await fetch('/api/notifications/prefs/accept-coda-error-groups/', {
    method: 'POST',
    credentials: 'include',
  })
  return parseJson(res)
}

export async function fetchRobotNotificationPrefs(): Promise<UserRobotNotificationPrefs[]> {
  const res = await fetch('/api/notifications/prefs/robots/', { credentials: 'include' })
  return parseJson(res)
}

export async function putRobotNotificationPrefs(
  robotId: number,
  data: UserRobotNotificationPrefsWrite
): Promise<UserRobotNotificationPrefs> {
  const res = await fetch(`/api/notifications/prefs/robots/${robotId}/`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  })
  return parseJson(res)
}

export async function deleteRobotNotificationPrefs(robotId: number): Promise<void> {
  const res = await fetch(`/api/notifications/prefs/robots/${robotId}/`, {
    method: 'DELETE',
    credentials: 'include',
  })
  await parseJson(res)
}
